import React, { useRef, useState, useEffect, useCallback } from "react";
import { useScopeParams } from "@/hooks/useScopeParams";
import { RightOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import WcIcon from "../../../assets/icons/wc.svg?react";
import FingerprintIcon from "../../../assets/icons/fingerprint.svg?react";
import CakeIcon from "../../../assets/icons/cake.svg?react";
import VirusIcon from "../../../assets/icons/virus.svg?react";
import FlagIcon from "../../../assets/icons/app_flag.svg?react";
import CalendarIcon from "../../../assets/icons/calendar.svg?react";
import { getCellRenderer } from "../../../utils/tableConfigs/OverviewFunctions";
import { trials } from "@/utils/trials";
import { SubjectHeaderMarker } from "@/components/UI/DiscrepancyMarker";

const getDisplayValue = (value?: string | null) => {
  return !value || value === "" || value === "-" ? "-" : value;
};

// Prefer the calculated current age (from birth_date). When the schema carries
// no birth anchor — e.g. Clario loads a source-reported age instead of a DOB —
// fall back to that reported age (with its unit) so the profile still shows one.
const getAgeDisplay = (info: {
  current_age?: number | string | null;
  age?: number | string | null;
  age_unit?: string | null;
}): string | null => {
  if (info.current_age != null && info.current_age !== "") {
    return String(info.current_age);
  }
  if (info.age != null && info.age !== "") {
    return info.age_unit ? `${info.age} ${info.age_unit}` : String(info.age);
  }
  return null;
};

type LabelItemProps = {
  label: string;
  value: React.ReactNode;
};

const LabelItem = ({ label, value }: LabelItemProps) => (
  <div className="flex items-center gap-[6px] whitespace-nowrap">
    <span
      className="text-[14px] font-normal tracking-[-0.14px]"
      style={{ color: "rgba(0, 0, 0, 0.45)" }}
    >
      {label}
    </span>
    <span className="text-[14px] font-medium tracking-[-0.14px] text-[#2B4752]">
      {value ?? "-"}
    </span>
  </div>
);

type IconItemProps = {
  icon: React.ReactNode;
  value: React.ReactNode;
  tooltip?: string;
};

const IconItem = ({ icon, value, tooltip }: IconItemProps) => {
  const iconNode = (
    <span
      className="flex items-center shrink-0"
      style={{ color: "rgba(0, 0, 0, 0.45)" }}
    >
      {icon}
    </span>
  );
  return (
    <div className="flex items-center gap-1 whitespace-nowrap icon-small">
      {tooltip ? <Tooltip title={tooltip}>{iconNode}</Tooltip> : iconNode}
      <span
        className="text-[14px] font-normal tracking-[-0.14px]"
        style={{ color: "rgba(0, 0, 0, 0.85)", lineHeight: "24px" }}
      >
        {value ?? "-"}
      </span>
    </div>
  );
};

export default function SingleSubjectCardHeader({
  subjectInfo,
}: {
  subjectInfo: any;
  sidebarCollapsed?: boolean;
}) {
  const { id, subjectId } = useScopeParams();
  const scrollRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // "right" = can scroll right, "left" = scrolled to end, can go back, "none" = no overflow
  const [arrowDirection, setArrowDirection] = useState<
    "right" | "left" | "none"
  >("none");

  const checkOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const canScrollRight = el.scrollWidth > el.clientWidth + el.scrollLeft + 1;
    const canScrollLeft = el.scrollLeft > 1;

    if (canScrollRight) {
      setArrowDirection("right");
    } else if (canScrollLeft) {
      setArrowDirection("left");
    } else {
      setArrowDirection("none");
    }
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [checkOverflow]);

  const handleArrowClick = () => {
    isScrollingRef.current = true;
    clearTimeout(scrollTimeoutRef.current);

    if (arrowDirection === "right") {
      setArrowDirection("left");
      scrollRef.current?.scrollTo({
        left: scrollRef.current.scrollWidth,
        behavior: "smooth",
      });
    } else {
      setArrowDirection("right");
      scrollRef.current?.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    }

    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 500);
  };

  const handleScroll = () => {
    if (isScrollingRef.current) return;
    checkOverflow();
  };

  return (
    <div
      className="flex h-[54px] p-3 justify-between items-center flex-1 text-[#2B4752]"
      data-testid="subject-information-bar"
      style={{
        borderRadius: 8,
        border: "1px solid rgba(0, 0, 0, 0.04)",
        background: "#FFF",
        boxShadow: "0 2px 15px 4px rgba(0, 0, 0, 0.02)",
      }}
    >
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 min-w-0 overflow-x-auto hide-scrollbar flex items-center gap-5"
      >
        <SubjectHeaderMarker subjectId={subjectId} />
        <LabelItem
          label="Status"
          value={(getCellRenderer as any)(subjectInfo.study_status ?? "-")}
        />
        <LabelItem label="Site" value={subjectInfo.site} />
        {id !== trials.bridgebio.PROPEL && (
          <LabelItem
            label="Study Arm"
            value={getDisplayValue(subjectInfo.study_arm_label)}
          />
        )}
        {id === trials.bridgebio.PROPEL && (
          <LabelItem
            label="Study Group"
            value={subjectInfo.subject_group ?? "-"}
          />
        )}
        <IconItem
          icon={<WcIcon width={18} height={18} />}
          value={subjectInfo.sex}
          tooltip="Sex"
        />
        <IconItem
          icon={<FingerprintIcon width={28} height={28} />}
          value={subjectInfo.race}
          tooltip="Race"
        />
        <IconItem
          icon={<CakeIcon width={28} height={28} />}
          value={getAgeDisplay(subjectInfo)}
          tooltip="Age"
        />
        <IconItem
          icon={<CalendarIcon width={28} height={28} />}
          value={subjectInfo.yob}
          tooltip="Year of Birth"
        />
        <IconItem
          icon={<FlagIcon width={28} height={28} />}
          value={subjectInfo.country}
          tooltip="Country"
        />
        <IconItem
          icon={<VirusIcon width={28} height={28} />}
          value={subjectInfo.indication}
          tooltip="Indication"
        />
      </div>

      {arrowDirection !== "none" && (
        <button
          onClick={handleArrowClick}
          className="flex items-center justify-center p-1 cursor-pointer border-none bg-transparent rounded-sm shrink-0"
          data-testid="subject-info-toggle-button"
          style={{ color: "rgba(0, 0, 0, 0.25)" }}
        >
          <RightOutlined
            style={{
              fontSize: 16,
              transform:
                arrowDirection === "left" ? "rotate(180deg)" : undefined,
              transition: "transform 0.2s ease",
            }}
          />
        </button>
      )}
    </div>
  );
}
